import { DodoPayments } from 'dodopayments-checkout'
import { api } from './api'

const DODO_MODE = import.meta.env.VITE_DODO_MODE || 'test'

export const PRODUCT_STATUSES = {
  launched: { label: 'Launched', short: 'Launched' },
  'releasing-soon': { label: 'Releasing soon', short: 'Soon' },
  'in-research': { label: 'In progress R&D', short: 'In R&D' },
}

export const STATUS_ORDER = ['launched', 'releasing-soon', 'in-research']

export const PRODUCTS = [
  // ── Launched ────────────────────────────────────────────────────────────
  {
    id: 'ideoscreen',
    name: 'IDEOSCREEN',
    status: 'launched',
    category: 'Creative development & production',
    tagline: 'Creative storytelling and production platform',
    headline: 'From First Idea to Final Production.',
    description:
      'From scripts and storyboards to schedules, crew org and team collaboration — IDEOSCREEN keeps the whole development process inside one connected workspace.',
    whatIs:
      'IDEOSCREEN is a creative storytelling and production platform designed to take projects from initial concept through development, planning and production. It brings the tools for developing stories, writing scripts, building storyboards, organising production and collaborating with a team into one connected workspace, built for film, television, streaming, theatre, animation, comics, novels, documentaries and digital content.',
    useCases: [
      'Screenwriting and storytelling: develop concepts, characters, narratives, treatments and scripts.',
      'Storyboarding and visual planning: translate written ideas into planned scenes and visual sequences.',
      'Production planning: organise scenes, schedules, tasks, crew requirements and project information.',
      'Creative collaboration: bring writers, directors, producers and production teams into a shared workflow.',
      'Multi-format development: build and manage projects for cinema, TV, streaming, theatre, animation, comics, novels and digital media.',
      'AI-assisted creation: explore ideas, refine writing and support creative development with integrated AI tools, keeping the creator at the centre of the process.',
      'Project and intellectual-property records: use supported registry and documentation features to organise project information.',
    ],
    sectorBenefits: {
      consumer:
        'Writers, aspiring filmmakers, authors and independent creators can develop their ideas in a structured way — from early drafts to storyboards and production plans — without juggling disconnected tools.',
      enterprise:
        'Production houses, studios, agencies, publishers and media companies can organise creative development, coordinate contributors and improve visibility across project workflows, bringing creative preparation and production planning together.',
      government:
        'Public broadcasters, educational institutions, cultural organisations and government communications teams can use suitable workflows to develop documentaries, educational media and public-information campaigns.',
    },
    value:
      'Give creative projects a connected path from storytelling and development to production planning, helping creators spend less effort managing workflows and more effort making the work.',
    icon: 'Clapperboard',
    color: '#ec4899',
    preview: null,
    plans: [],
    sellable: false,
  },
  {
    id: 'sceneviz',
    name: 'SCENEVIZ',
    status: 'launched',
    category: 'AI creative & video generation',
    tagline: 'AI creative and video generation platform',
    headline: 'Ideas Into Moving Worlds.',
    description:
      'Generate moving visuals from ideas — cinematic environments, campaign assets and AI-assisted video content, from concept exploration to social-ready output.',
    whatIs:
      'SCENEVIZ is an AI-powered creative platform designed to turn ideas into visual content and moving imagery. It brings AI-assisted visual creation to professional creators, filmmakers, marketers and social content producers — from cinematic environments and imaginative worlds to promotional content and social videos.',
    useCases: [
      'Cinematic visual creation: explore environments, scenes, moods and concepts for film and digital storytelling.',
      'AI-assisted video generation: create moving visual content from supported prompts and inputs.',
      'Concept visualisation: explore ideas and visual directions before committing to full production.',
      'Marketing and advertising: develop visual assets and video concepts for campaigns, brands and products.',
      'Social media content: create engaging visual material for digital channels.',
      'Pre-production support: pitch decks, concept development and visual ideation alongside existing production tools.',
    ],
    sectorBenefits: {
      consumer:
        'Make visual ideas tangible without a full traditional production setup — experiment with concepts, produce visual content and explore new creative directions with the platform’s supported generation tools.',
      enterprise:
        'Marketing teams, agencies and production companies can explore campaign concepts, develop visual prototypes and produce AI-assisted content for suitable workflows, shortening concept iteration depending on project complexity.',
      government:
        'Communications teams, cultural organisations and educational institutions can explore visual storytelling, public-awareness content and educational videos, subject to accuracy, accessibility and content-governance requirements.',
    },
    value:
      'Turn visual imagination into moving imagery, helping creators explore more possibilities and develop compelling content through AI-assisted creation.',
    icon: 'Video',
    color: '#8b5cf6',
    preview: null,
    plans: [],
    sellable: false,
  },
  {
    id: 'aimg',
    name: 'AIMG',
    status: 'launched',
    category: 'AI model garage',
    tagline: 'OPRAIZ’s own AI models, one garage',
    headline: 'Every OPRAIZ Model. One Garage.',
    description:
      'A direct alternative to multi-provider model routers: AIMG serves a growing suite of AI models built and owned by OPRAIZ through a single account and one unified interface.',
    whatIs:
      'AIMG (AI Model Garage) is OPRAIZ’s model-access platform — a direct alternative to third-party model routers and aggregators. Instead of routing your work through outside providers, AIMG serves a growing suite of AI models built and owned by OPRAIZ, accessed through a single account and one unified interface.',
    useCases: [
      'Access OPRAIZ’s own AI models — text, code, document and creative work — through a single unified interface.',
      'Build applications on OPRAIZ models instead of assembling and maintaining multiple third-party providers.',
      'Route supported tasks to the OPRAIZ model that fits the job, with one account, one setup.',
      'Compare outputs across OPRAIZ models and iterate, where supported.',
      'Enterprise and government: procure model access directly from the builder, subject to data-handling and governance requirements.',
    ],
    sectorBenefits: {
      consumer:
        'Use OPRAIZ’s own AI models for writing, coding, research and creative work through one account — no provider juggling, no third-party middle layer.',
      enterprise:
        'Give teams direct access to OPRAIZ-owned models for product and internal workflows, with procurement and support handled by one vendor instead of many providers.',
      government:
        'Deploy OPRAIZ-owned models under direct agreements with the builder, subject to applicable data-handling, security and procurement requirements.',
    },
    value:
      'Get the convenience of a model marketplace with the accountability of a single owner — OPRAIZ.',
    icon: 'Boxes',
    color: '#06b6d4',
    preview: null,
    plans: [],
    sellable: false,
  },
  // ── Releasing soon ─────────────────────────────────────────────────────
  {
    id: 'orbo-diya',
    name: 'ORBO DIYA',
    status: 'releasing-soon',
    category: 'AI workforce',
    tagline: 'An AI workforce, without boundaries',
    headline: 'An AI Workforce. Without Boundaries.',
    description:
      'More than an assistant: conversational intelligence plus specialised AI agents, reasoning, writing, research and task execution — with supported background work that continues around the clock.',
    whatIs:
      'ORBO DIYA reimagines AI as more than an assistant. It is an AI workforce built around a conversational intelligence core and an ecosystem of specialised AI agents, bringing reasoning, research, writing, creativity, problem-solving and task execution into one unified experience. Supported cloud-based tasks can continue running around the clock, even when the user’s device is switched off.',
    useCases: [
      'Personal productivity, learning, planning and creative projects.',
      'Research, writing, coding and complex problem-solving.',
      'Delegation and coordination of supported multi-step tasks.',
      'Background task execution and workflows that continue beyond an active session.',
      'Business process assistance, administrative work and cross-functional operations.',
    ],
    sectorBenefits: {
      consumer:
        'Access a versatile AI workforce for everyday tasks, personal projects, learning and creativity, including supported work that continues while the device is switched off.',
      enterprise:
        'Extend team capacity with AI-assisted workflows, specialised agents and background task execution, subject to integrations, permissions and operational controls.',
      government:
        'Support suitable research, document processing, administrative workflows and public-service operations, subject to security, governance and accountability requirements.',
    },
    value:
      'Move beyond asking AI questions towards delegating work and coordinating AI capabilities across everyday and professional operations.',
    icon: 'Sparkles',
    color: '#f59e0b',
    preview: null,
    plans: [],
    sellable: false,
  },
  {
    id: 'baanzon-chat',
    name: 'BAANZON CHAT',
    status: 'releasing-soon',
    category: 'AI assistant',
    tagline: 'Big thinking. Practical results.',
    headline: 'Big Thinking. Practical Results.',
    description:
      'A general-purpose AI assistant for reasoning, writing, coding, research and analysis — built to turn thinking into usable work through one conversational interface.',
    whatIs:
      'BAANZON CHAT is a general-purpose AI assistant built to help people think through problems, work with information and produce useful results. Its focus spans reasoning, writing, coding, research and analysis — supporting tasks that require more than a straightforward answer, in an interface designed for everyday users and professionals alike.',
    useCases: [
      'Research, information synthesis and document analysis.',
      'Writing reports, proposals, communications and presentations.',
      'Coding assistance, debugging and software development.',
      'Business planning, strategic analysis and decision support.',
      'Learning, explanation and structured problem-solving.',
    ],
    sectorBenefits: {
      consumer:
        'Learn new subjects, improve writing, research unfamiliar topics and tackle personal, academic and creative challenges with accessible AI assistance.',
      enterprise:
        'Help teams accelerate research, prepare business documents, analyse information and support knowledge-intensive work. Human review remains essential for consequential outputs.',
      government:
        'Assist with policy research, document summarisation, report preparation and analysis of suitable information, with deployment aligned to applicable data-handling requirements.',
    },
    value:
      'Reduce the effort required to move from questions and information to structured thinking and usable work.',
    icon: 'MessagesSquare',
    color: '#38bdf8',
    preview: null,
    plans: [],
    sellable: false,
  },
  {
    id: 'octopus-shield',
    name: 'OCTOPUS SHIELD',
    status: 'releasing-soon',
    category: 'Cybersecurity',
    tagline: 'Adaptive cybersecurity intelligence',
    headline: 'The Best Defense Doesn’t Interrupt Work.',
    description:
      'Adaptive defense intelligence built to keep protection aligned with systems, users and operations — security that supports productivity instead of standing in its way.',
    whatIs:
      'OCTOPUS SHIELD is OPRAIZ’s cybersecurity product, built around the principle of adaptive defense intelligence. It is intended to help organisations address digital security risks while keeping protection aligned with their systems, users and operational requirements — making security a practical part of digital operations rather than an obstacle to productive work. Specific protective capabilities depend on the deployed product configuration.',
    useCases: [
      'Cybersecurity risk identification and prioritisation, where supported.',
      'Protection of relevant digital systems, applications and assets.',
      'Support for IT and security teams reviewing security signals and investigating incidents, where implemented.',
      'Security oversight and remediation prioritisation.',
      'Cyber-risk management aligned with business continuity and operational requirements.',
    ],
    sectorBenefits: {
      consumer:
        'Where consumer-facing protection is available, help individuals address relevant digital threats and improve their approach to online security.',
      enterprise:
        'Support risk visibility, security operations and prioritisation of protective action to help organisations manage exposure and maintain operational continuity.',
      government:
        'Support appropriate cybersecurity and resilience efforts for public-sector systems and digital services, subject to security standards, procurement requirements and deployment approval.',
    },
    value:
      'Make cybersecurity a more integrated part of operations, helping organisations manage digital risk without losing sight of productivity.',
    icon: 'ShieldCheck',
    color: '#4f46e5',
    preview: null,
    plans: [],
    sellable: false,
  },
  // ── In progress R&D ────────────────────────────────────────────────────
  {
    id: 'squamed',
    name: 'SQUAMED',
    status: 'in-research',
    tagline: 'The complete OS for hospitals',
    description: 'End-to-end hospital management with patient records, departments, pharmacy, labs, billing, and AI-powered analytics.',
    icon: 'Hospital',
    color: '#0d9488',
    variant: 'squamed',
    preview: '/mockups/squamed.svg',
    sellable: false,
    plans: [
      {
        id: 'squamed-starter',
        name: 'Starter',
        tagline: 'For single-department hospitals',
        monthly: { price: 4999, currency: 'INR' },
        annual: { price: 3999, currency: 'INR', discount: 20 },
        features: [
          'Up to 5 users',
          'Up to 2,000 patient records',
          'Single department workflows',
          'Appointments & scheduling',
          'Basic billing & receipts',
          'E-prescriptions',
          'Email support',
          'ORBO DIYA AI assistant',
        ],
        cta: 'Start with Starter',
        popular: false,
      },
      {
        id: 'squamed-growth',
        name: 'Growth',
        tagline: 'For multi-department hospitals',
        monthly: { price: 14999, currency: 'INR' },
        annual: { price: 11999, currency: 'INR', discount: 20 },
        features: [
          'Up to 25 users',
          'Unlimited patient records',
          'Multi-department workflows',
          'Advanced analytics & dashboards',
          'Pharmacy & lab integration',
          'Insurance & TPA billing',
          'Priority support',
          'ORBO DIYA AI assistant',
          'API access',
        ],
        cta: 'Start with Growth',
        popular: true,
      },
      {
        id: 'squamed-enterprise',
        name: 'Enterprise',
        tagline: 'For multispeciality hospitals',
        monthly: { price: null, currency: 'INR' },
        annual: { price: null, currency: 'INR' },
        features: [
          'Unlimited users',
          'Unlimited everything',
          'Custom integrations & API',
          'Dedicated account manager',
          'SLA & uptime guarantee',
          'On-premise deployment option',
          'Custom AI model training',
          'SSO & advanced security',
          'Compliance & audit tools',
          'ORBO DIYA AI assistant',
        ],
        cta: 'Contact Sales',
        popular: false,
      },
    ],
  },
  {
    id: 'squamed-minime',
    name: 'SQUAMED LITE',
    status: 'in-research',
    tagline: 'The complete OS for clinics, pharmacies & labs',
    description: 'Lightweight yet powerful OS for single clinics, pharmacies, and diagnostic labs. All essential features, zero complexity.',
    icon: 'Activity',
    color: '#14b8a6',
    variant: 'squamedminime',
    preview: '/mockups/squamed-minime.svg',
    sellable: false,
    plans: [
      {
        id: 'squamed-minime-starter',
        name: 'Starter',
        tagline: 'For single clinics & pharmacies',
        monthly: { price: 2999, currency: 'INR' },
        annual: { price: 2399, currency: 'INR', discount: 20 },
        features: [
          'Up to 2 users',
          'Up to 1,000 patient records',
          'Appointments & scheduling',
          'Basic billing & receipts',
          'E-prescriptions',
          'Email support',
          'ORBO DIYA AI assistant',
        ],
        cta: 'Start with Starter',
        popular: false,
      },
      {
        id: 'squamed-minime-growth',
        name: 'Growth',
        tagline: 'For multi-doctor clinics & labs',
        monthly: { price: 7999, currency: 'INR' },
        annual: { price: 6399, currency: 'INR', discount: 20 },
        features: [
          'Up to 10 users',
          'Unlimited patient records',
          'Multi-doctor scheduling',
          'Advanced billing & insurance',
          'Pharmacy & lab integration',
          'Priority support',
          'ORBO DIYA AI assistant',
          'API access',
        ],
        cta: 'Start with Growth',
        popular: true,
      },
    ],
  },
  {
    id: 'squadent',
    name: 'SQUADENT',
    status: 'in-research',
    tagline: 'The complete OS for dental institutions',
    description: 'Purpose-built for dental colleges, hospitals, and multi-chair institutions. Clinical workflows, patient management, and AI diagnostics.',
    icon: 'Stethoscope',
    color: '#b8860b',
    variant: 'squadent',
    preview: '/mockups/squadent.svg',
    sellable: false,
    plans: [
      {
        id: 'squadent-starter',
        name: 'Starter',
        tagline: 'For single-department dental units',
        monthly: { price: 4999, currency: 'INR' },
        annual: { price: 3999, currency: 'INR', discount: 20 },
        features: [
          'Up to 5 users',
          'Up to 2,000 patient records',
          'Chair & appointment scheduling',
          'Treatment planning',
          'Basic billing & receipts',
          'E-prescriptions',
          'Email support',
          'ORBO DIYA AI assistant',
        ],
        cta: 'Start with Starter',
        popular: false,
      },
      {
        id: 'squadent-growth',
        name: 'Growth',
        tagline: 'For multi-chair dental institutions',
        monthly: { price: 14999, currency: 'INR' },
        annual: { price: 11999, currency: 'INR', discount: 20 },
        features: [
          'Up to 25 users',
          'Unlimited patient records',
          'Multi-chair workflows',
          'Advanced analytics & dashboards',
          'Imaging & radiology integration',
          'Insurance & TPA billing',
          'Priority support',
          'ORBO DIYA AI assistant',
          'API access',
        ],
        cta: 'Start with Growth',
        popular: true,
      },
      {
        id: 'squadent-enterprise',
        name: 'Enterprise',
        tagline: 'For dental colleges & chains',
        monthly: { price: null, currency: 'INR' },
        annual: { price: null, currency: 'INR' },
        features: [
          'Unlimited users',
          'Unlimited everything',
          'Custom integrations & API',
          'Dedicated account manager',
          'SLA & uptime guarantee',
          'On-premise deployment option',
          'Custom AI model training',
          'SSO & advanced security',
          'Compliance & audit tools',
          'ORBO DIYA AI assistant',
        ],
        cta: 'Contact Sales',
        popular: false,
      },
    ],
  },
  {
    id: 'squadent-minime',
    name: 'SQUADENT LITE',
    status: 'in-research',
    tagline: 'The complete OS for dental practices',
    description: 'Streamlined dental practice management for solo practitioners and small clinics. Simple, fast, and AI-powered.',
    icon: 'Microscope',
    color: '#e8756a',
    variant: 'squadentminime',
    preview: '/mockups/squadent-minime.svg',
    sellable: false,
    plans: [
      {
        id: 'squadent-minime-starter',
        name: 'Starter',
        tagline: 'For solo dental practitioners',
        monthly: { price: 2999, currency: 'INR' },
        annual: { price: 2399, currency: 'INR', discount: 20 },
        features: [
          'Up to 2 users',
          'Up to 1,000 patient records',
          'Chair & appointment scheduling',
          'Treatment planning',
          'Basic billing & receipts',
          'Email support',
          'ORBO DIYA AI assistant',
        ],
        cta: 'Start with Starter',
        popular: false,
      },
      {
        id: 'squadent-minime-growth',
        name: 'Growth',
        tagline: 'For multi-chair dental practices',
        monthly: { price: 7999, currency: 'INR' },
        annual: { price: 6399, currency: 'INR', discount: 20 },
        features: [
          'Up to 10 users',
          'Unlimited patient records',
          'Multi-chair scheduling',
          'Advanced billing & insurance',
          'Imaging integration',
          'Priority support',
          'ORBO DIYA AI assistant',
          'API access',
        ],
        cta: 'Start with Growth',
        popular: true,
      },
    ],
  },
  {
    id: 'nammawara-enterprise',
    name: 'NAMMAWARA ENTERPRISE',
    status: 'in-research',
    tagline: 'Government-grade OS for large-scale deployments',
    description: 'Enterprise platform designed for state and central government hospitals, public health systems, and large-scale institutional deployments.',
    icon: 'Building2',
    color: '#1e40af',
    variant: 'nammawara',
    preview: '/mockups/nammawara-enterprise.svg',
    sellable: false,
    plans: [
      {
        id: 'nammawara-enterprise-custom',
        name: 'Custom',
        tagline: 'Tailored for government institutions',
        monthly: { price: null, currency: 'INR' },
        annual: { price: null, currency: 'INR' },
        features: [
          'Unlimited users & departments',
          'Custom deployment (cloud or on-premise)',
          'Government compliance & audit tools',
          'SSO & advanced security',
          'Dedicated account manager',
          'SLA & uptime guarantee',
          'Custom AI model training',
          'API & third-party integrations',
          'Multi-language support',
          '24/7 priority support',
        ],
        cta: 'Contact Sales',
        popular: false,
      },
    ],
  },
  {
    id: 'nammawara-ops',
    name: 'NAMMAWARA OPS',
    status: 'in-research',
    tagline: 'Operations management for government',
    description: 'Streamlined operations, inventory, workforce, and logistics management for government healthcare facilities and public institutions.',
    icon: 'Shield',
    color: '#7c3aed',
    variant: 'nammawara',
    preview: '/mockups/nammawara-ops.svg',
    sellable: false,
    plans: [
      {
        id: 'nammawara-ops-custom',
        name: 'Custom',
        tagline: 'Tailored for government operations',
        monthly: { price: null, currency: 'INR' },
        annual: { price: null, currency: 'INR' },
        features: [
          'Unlimited users & facilities',
          'Inventory & supply chain management',
          'Workforce & shift scheduling',
          'Logistics & procurement tracking',
          'Government compliance tools',
          'SSO & advanced security',
          'Dedicated account manager',
          'Custom integrations & API',
          'Multi-language support',
          '24/7 priority support',
        ],
        cta: 'Contact Sales',
        popular: false,
      },
    ],
  },
]

export function getProductBySlug(slug) {
  return PRODUCTS.find((p) => p.id === slug) || null
}

export function getPlanById(planId) {
  for (const product of PRODUCTS) {
    const plan = product.plans.find((p) => p.id === planId)
    if (plan) return { plan, product }
  }
  return null
}

export function getAllPlans() {
  const all = []
  for (const product of PRODUCTS) {
    for (const plan of product.plans) {
      all.push({ ...plan, productId: product.id, productName: product.name })
    }
  }
  return all
}

const currencyFormatter = (currency, locale) =>
  new Intl.NumberFormat(locale || undefined, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  })

// Best-effort currency + locale chosen from the browser so users worldwide see
// prices in a familiar region/locale without changing what's actually charged.
function userLocale() {
  try {
    return navigator.language
  } catch {
    return 'en-IN'
  }
}

function detectCurrency() {
  try {
    const region = (Intl.DateTimeFormat().resolvedOptions().timeZone || '').split('/')[0]
    const map = { Asia: 'INR', America: 'USD', Europe: 'EUR', Africa: 'NGN', Oceania: 'AUD' }
    if (map[region]) return map[region]
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency: 'USD' })
        .formatToParts(0).find((p) => p.type === 'currency')?.value || 'USD'
    } catch {
      return 'INR'
    }
  } catch {
    return 'INR'
  }
}

export function formatPrice(amount, currency = 'INR') {
  if (amount === null) return 'Custom'
  // Give wallets a sanitized, non-breaking-safe render globally.
  return currencyFormatter(currency, currency === 'INR' ? 'en-IN' : userLocale()).format(amount)
}

// The currency a plan is actually billed in. Today plans are INR-denominated;
// a global 'display' currency is offered only for presentational formatting.
export const DEFAULT_DISPLAY_CURRENCY = detectCurrency()

export function productsByStatus(status) {
  return PRODUCTS.filter((p) => p.status === status)
}

// Self-serve purchase is unavailable for the entire current catalog: the
// launched products ship without self-serve pricing yet, and the R&D lines
// are deliberately not sold online. All purchase CTAs therefore route to
// contact/waitlist. Checkout machinery stays live for existing subscribers
// (plan changes / cancellations run through manage-subscription, which does
// not consult this flag).
export function isProductSellable(product) {
  if (!product || product.sellable === false) return false
  return product.plans.some((p) => !isPlanContactOnly(p))
}

// 'checkout' | 'waitlist' | 'contact' — the CTA destination every pricing
// surface should honour instead of hardcoding /checkout links.
export function productSalesMode(product) {
  if (isProductSellable(product)) return 'checkout'
  return product?.status === 'releasing-soon' ? 'waitlist' : 'contact'
}

export function isProductContactOnly(product) {
  return !isProductSellable(product)
}

export function isPlanContactOnly(plan) {
  return plan.monthly?.price === null && plan.annual?.price === null
}

export function initDodoCheckout() {
  if (!DodoPayments?.Initialize) return
  DodoPayments.Initialize({
    mode: DODO_MODE,
    displayType: 'overlay',
    onEvent: (event) => {
      switch (event.event_type) {
        case 'checkout.opened':
          if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('dodo:checkout-opened'))
          break
        case 'checkout.closed':
          if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('dodo:checkout-closed'))
          break
        case 'checkout.error':
          // Meaningful: a real checkout failure the user just hit. Kept
          // ungated so it is visible in prod consoles alongside the
          // 'dodo:checkout-error' window event dispatched below.
          console.error('[dodo] checkout error:', event)
          if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('dodo:checkout-error', { detail: event }))
          break
        case 'checkout.redirect':
          if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('dodo:checkout-redirect'))
          break
        default:
          if (event?.event_type && import.meta.env.DEV) console.debug('[dodo] unhandled event:', event.event_type)
      }
    },
  })
}

export async function openDodoCheckout(checkoutUrl) {
  if (!DodoPayments?.Checkout?.open) throw new Error('Dodo Payments SDK not loaded')
  await DodoPayments.Checkout.open({
    checkoutUrl,
    options: {
      showTimer: true,
      showSecurityBadge: true,
    },
  })
}

export async function createCheckoutSession({ planId, productId, period, provider, email, supabase, couponCode }) {
  if (!supabase) {
    if (import.meta.env.DEV) console.warn('[payments] createCheckoutSession called with no backend configured')
    throw new Error('Billing is not available right now because the live backend is not configured. Please try again in a moment.')
  }
  const { data, error } = await supabase.functions.invoke('create-checkout', {
    body: { planId, productId, period, provider, email, ...(couponCode ? { couponCode } : {}) },
  })
  if (error) throw error
  return data
}

// Simulated checkout for demo/test mode — creates the subscription + payment +
// invoice directly so the full flow can be experienced without live gateway keys.
export async function demoCheckout({ planId, productId, period, supabase, couponCode }) {
  if (!supabase) {
    if (import.meta.env.DEV) console.warn('[payments] demoCheckout called with no backend configured')
    throw new Error('Billing is not available right now. Please try again in a moment.')
  }
  const { data, error } = await supabase.functions.invoke('demo-checkout', {
    body: { planId, productId, period, ...(couponCode ? { couponCode } : {}) },
  })
  if (error) throw error
  return data
}

// Subscription lifecycle actions — calls the manage-subscription edge function.
// Returns { subscription, message, redirectUrl? } on success.
// For a 'change' action, pass newPlanId/newPeriod to switch plan.
export async function manageSubscription({ subscriptionId, action, planId, newPlanId, newPeriod }) {
  return api('manage-subscription', {
    subscription_id: subscriptionId,
    action,
    ...(planId ? { plan_id: planId } : {}),
    ...(newPlanId ? { new_plan_id: newPlanId } : {}),
    ...(newPeriod ? { new_period: newPeriod } : {}),
  })
}

export const PAYMENT_PROVIDERS = [
  {
    id: 'dodo',
    name: 'Dodo Payments',
    description: 'Cards, wallets & global payment methods',
    badge: 'Global',
    icon: 'Globe',
  },
  {
    id: 'cashfree',
    name: 'Cashfree',
    description: 'UPI, netbanking, cards & wallets (India)',
    badge: 'India',
    icon: 'CreditCard',
  },
]
